import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import mapApiObjectToCamelCase, {
  ApiObject,
} from '~/libs/api-hooks/architect/utils/mapApiObjectToCamelCase';
import requester, {
  UrlOptions,
} from '~/libs/api-hooks/architect/utils/requester';
import StatusError from '~/libs/api-hooks/architect/utils/statusError';
import { QueryMap } from '~/libs/api-hooks/architect/utils/prepareSearchParams';
import DeepPartial from '~/libs/api-hooks/architect/utils/DeepPartial';
import ExceptionLogger from '~/libs/api-hooks/architect/backplane/makeExceptionLogger';

export interface fetchReturn<DeepPartialItem> {
  items: DeepPartialItem[];
  nextPageToken: string;
  pageParam: string;
}

export type PaginatedTokenApiOptions<
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
> = Omit<
  UseInfiniteQueryOptions<fetchReturn<DeepPartialItem>, StatusError>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'getPreviousPageParam'
>;

export type PaginatedTokenApiResult<
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
> = UseInfiniteQueryResult<fetchReturn<DeepPartialItem>, StatusError>;

export interface UsePaginatedTokenApiProps<
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
> {
  pathname: string;
  cacheKey: string[];
  pageTokenQueryIndex?: string;
  queryMap?: QueryMap;
  reactQueryOptions?: PaginatedTokenApiOptions<Item, DeepPartialItem>;
  urlOptions?: UrlOptions;
  includeApiKeyWhenAvailable?: boolean;
  customHeaders?: Record<string, string>;
}

const usePaginatedTokenApi = <
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
>({
  pathname,
  cacheKey,
  pageTokenQueryIndex = 'next_page_token',
  queryMap,
  reactQueryOptions = {},
  urlOptions,
  includeApiKeyWhenAvailable = true,
  customHeaders,
}: UsePaginatedTokenApiProps<Item, DeepPartialItem>): PaginatedTokenApiResult<
  Item,
  DeepPartialItem
> => {
  const getNextPageParam = (currentPage: fetchReturn<DeepPartialItem>) => {
    return currentPage.nextPageToken !== ''
      ? currentPage.nextPageToken
      : undefined;
  };

  const getPreviousPageParam = () => {
    return undefined;
  };

  const fetchAPI = async ({
    pageParam = '',
  }): Promise<fetchReturn<DeepPartialItem>> => {
    if (pageParam) {
      queryMap = {
        ...queryMap,
        [pageTokenQueryIndex]: pageParam,
      };
    }
    const response = await requester({
      pathname,
      queryMap,
      method: 'GET',
      urlOptions,
      includeApiKeyWhenAvailable,
      sendCsrfToken: false,
      customHeaders,
    });

    try {
      const responseJson = await response.json();
      if (!Array.isArray(responseJson.items)) {
        throw new Error(
          'usePaginatedTokenApi expects an array in items to be returned',
        );
      }
      const responseParsed = responseJson.items.map((repo: ApiObject) =>
        mapApiObjectToCamelCase<DeepPartialItem>(repo),
      );

      return {
        items: responseParsed,
        nextPageToken: responseJson.next_page_token,
        pageParam,
      };
    } catch (error: any) {
      ExceptionLogger().error(error.message, { error });
      throw new StatusError({
        message: error.message,
        status: 0,
        pathname,
      });
    }
  };

  return useInfiniteQuery<fetchReturn<DeepPartialItem>, StatusError>(
    cacheKey,
    fetchAPI,
    {
      getNextPageParam,
      getPreviousPageParam,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      ...reactQueryOptions,
    },
  );
};

export default usePaginatedTokenApi;
